"use client";

import { useLocale } from "next-intl";
import { useState } from "react";

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

export interface LegalSection {
  title: string;
  html: string;
}

export default function LegalLayout({
  title,
  subtitle,
  note,
  sections,
  isLoading = false,
}: {
  title: string;
  subtitle?: string;
  note?: string;
  sections: LegalSection[];
  isLoading?: boolean;
}) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const t = (a: string, e: string) => (isAr ? a : e);
  const [active, setActive] = useState(0);

  const scrollTo = (i: number) => {
    setActive(i);
    document.getElementById(`legal-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="najdi bg-background text-ink-900 min-h-screen" dir={isAr ? "rtl" : "ltr"}>
      {/* Header */}
      <section className="bg-clay-600 text-white">
        <div className="container mx-auto px-6 py-14 md:py-20 text-center">
          <h1 className="font-display text-3xl md:text-5xl font-extrabold mb-4">{title}</h1>
          {subtitle && <p className="text-white/85 max-w-2xl mx-auto">{subtitle}</p>}
          {note && (
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-1.5 text-xs font-bold">
              <Icon name="update" className="text-[16px]" />
              {note}
            </span>
          )}
        </div>
      </section>

      <div className="container mx-auto px-6 py-12">
        {isLoading ? (
          <div className="max-w-3xl mx-auto space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-24 rounded-2xl bg-clay-100/40 animate-pulse" />
            ))}
          </div>
        ) : sections.length === 0 ? (
          <p className="text-center text-ink-500 py-20">{t("لا يوجد محتوى بعد", "No content yet")}</p>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* TOC */}
            <aside className="hidden lg:block w-64 shrink-0 lg:sticky lg:top-24">
              <nav className="rounded-2xl border border-clay-100 bg-surface p-2 space-y-1">
                {sections.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => scrollTo(i)}
                    className={`w-full text-start px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                      active === i ? "bg-clay-600 text-white" : "text-ink-500 hover:bg-clay-50 hover:text-clay-600"
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Content */}
            <div className="flex-1 min-w-0 max-w-3xl space-y-6">
              {sections.map((s, i) => (
                <section
                  key={i}
                  id={`legal-${i}`}
                  className="rounded-2xl border border-clay-100 bg-surface p-6 md:p-8 scroll-mt-24"
                >
                  <h2 className="font-display text-xl font-bold text-ink-900 mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-clay-600/10 text-clay-600 text-sm font-bold">
                      {i + 1}
                    </span>
                    {s.title}
                  </h2>
                  <div
                    className="prose max-w-none text-ink-500 leading-loose [&_strong]:text-ink-900 [&_a]:text-clay-600 [&_ul]:list-disc [&_ul]:ps-5"
                    dangerouslySetInnerHTML={{ __html: s.html }}
                  />
                </section>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
